import { api, apiErrorMessage, useAuthStore } from '@bemor/web-kit';
import { CommentsPanel } from '@bemor/web-kit/ui';
import { useInfiniteQuery } from '@tanstack/react-query';

import type { Comment, CommentRequest } from '@/api/types';
import { P } from '@/lib/permissions';
import { useWrite } from '@/lib/queries';
import { useMe } from '@/lib/staff';

/**
 * The staff-only comment thread of one record (BM-43 §2.4; merchant, dispute and case pages per D1). Reading needs
 * `admin.cases.read`, writing `admin.cases.write`; merchants never see comments.
 */
export function Comments({
    subjectType,
    subjectId,
}: {
    subjectType: CommentRequest['subject_type'];
    subjectId: string;
}) {
    const can = useAuthStore((s) => s.can);
    const me = useMe();
    const readable = can(P.casesRead);
    // Oldest first, a page at a time: "Show more comments" follows the cursor to the newer ones.
    const query = useInfiniteQuery({
        queryKey: ['/staff/comments', subjectType, subjectId],
        queryFn: ({ pageParam }) =>
            api().list<Comment>('/staff/comments', {
                params: {
                    subject_type: subjectType,
                    subject_id: subjectId,
                    limit: 100,
                    cursor: pageParam,
                },
            }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (page) => page.nextCursor ?? undefined,
        enabled: readable,
    });
    const comments = query.data?.pages.flatMap((p) => p.items) ?? [];
    const add = useWrite(
        async (body: string) =>
            (
                await api().post<Comment>('/staff/comments', {
                    subject_type: subjectType,
                    subject_id: subjectId,
                    body,
                } satisfies CommentRequest)
            ).data,
        ['/staff/comments'],
    );
    if (!readable) return null;
    return (
        <div data-testid="comments">
            <CommentsPanel
                comments={comments.map((c) => ({
                    id: c.id,
                    body: c.body,
                    created_at: c.created_at,
                    author: { id: c.author.id, name: c.author.name ?? null },
                }))}
                loading={query.isPending}
                error={query.isError ? apiErrorMessage(query.error) : null}
                hasMore={query.hasNextPage}
                loadingMore={query.isFetchingNextPage}
                onLoadMore={() => void query.fetchNextPage()}
                canWrite={can(P.casesWrite)}
                meId={me}
                onAdd={async (body) => {
                    try {
                        await add.mutateAsync(body);
                    } catch (e) {
                        throw new Error(apiErrorMessage(e));
                    }
                }}
            />
        </div>
    );
}
